import { TextInput } from '@Components/common';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import styles from './CompanyAndCharityNumberFields.module.scss';
import { FundingTypes, GroupTypes } from '@Types';
import { VALIDATE_COMPANY_NUMBER_REGEX, CHARITY_NUMBER_REGEX } from '@Helpers';

export const CompanyAndCharityNumberFields = ({
  typeOfOrganization,
}: {
  typeOfOrganization: string;
}) => {
  const { t } = useTranslation('account');
  const { register, errors, watch } = useFormContext();

  const typeOfOrganizationEnum = useMemo(() => {
    return (
      FundingTypes[typeOfOrganization as keyof typeof FundingTypes] ||
      GroupTypes[typeOfOrganization as keyof typeof GroupTypes]
    );
  }, [typeOfOrganization]);

  const showFields = useMemo(() => {
    return [
      GroupTypes.REGISTERED_CHARITY,
      GroupTypes.SOCIAL_ENTERPRISE,
      FundingTypes.COMPANY,
      FundingTypes.FOUNDATION,
    ].includes(typeOfOrganizationEnum);
  }, [typeOfOrganizationEnum]);

  const companyNumber = watch('company_number');
  const charityNumber = watch('charity_number');
  const otherRegistrationNumber = watch('other_registration_number');

  if (!showFields) {
    return null;
  }

  return (
    <div className={styles.sideBySideInputContainer}>
      <TextInput
        label={t('tabs.organization.registered_details.company_number')}
        type="text"
        inputRef={register({
          pattern: {
            value: VALIDATE_COMPANY_NUMBER_REGEX,
            message: t('tabs.organization.registered_details.company_number_error'),
          },
          validate: (value: string): true | string => {
            if (value || charityNumber || otherRegistrationNumber) {
              return true;
            }
            return t('tabs.organization.registered_details.registration_number_error') as string;
          },
        })}
        name="company_number"
        error={!!errors.company_number}
        helperText={errors.company_number?.message}
      />
      <TextInput
        label={t('tabs.organization.registered_details.charity_number')}
        type="text"
        inputRef={register({
          pattern: {
            value: CHARITY_NUMBER_REGEX,
            message: t('tabs.organization.registered_details.charity_number_regex_error'),
          },
          validate: (value: string): true | string => {
            if (value || companyNumber || otherRegistrationNumber) {
              return true;
            }
            return t('tabs.organization.registered_details.registration_number_error') as string;
          },
        })}
        name="charity_number"
        error={!!errors.charity_number}
        helperText={errors.charity_number?.message}
      />
      <TextInput
        label={t('tabs.organization.registered_details.other_registration_number')}
        type="text"
        inputRef={register({
          validate: (value: string): true | string => {
            if (value || companyNumber || charityNumber) {
              return true;
            }
            return t('tabs.organization.registered_details.registration_number_error') as string;
          },
        })}
        name="other_registration_number"
        error={!!errors.other_registration_number}
        helperText={errors.other_registration_number?.message}
      />
    </div>
  );
};
